import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getHeaders } from '../../../lib/requests';
import Spinner from '../spinner';
import { XMarkIcon } from '@heroicons/react/24/outline';

const MenuModal = ({ isOpen, setIsOpen, selectMenus, value = [] }) => {
  const [menus, setMenus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMenus, setSelectedMenus] = useState([]);

  useEffect(() => {
    if (value && !isOpen) {
      setSelectedMenus(value);
    }
    if (!isOpen) return;
    fetchMenus();
  }, [isOpen, searchQuery, value]);

  const fetchMenus = async () => {
    setIsLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const apiToken = localStorage.getItem('accessToken');
      const params = searchQuery ? { name: searchQuery } : {};
      const response = await axios.get(`${apiUrl}/menus`, {
        headers: getHeaders(apiToken),
        params,
      });
      setMenus(response.data.data);
    } catch (error) {
      console.error('Error fetching menus', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const handleToggleMenu = (menu) => {
    let updated;
    if (selectedMenus.some((m) => m.menu_id === menu.menu_id)) {
      updated = selectedMenus.filter((m) => m.menu_id !== menu.menu_id);
    } else {
      updated = [...selectedMenus, menu];
    }
    setSelectedMenus(updated);
  };

  const handleSaveSelection = () => {
    selectMenus(selectedMenus);
    setIsOpen(false);
  };

  const handleClearMenus = () => {
    selectMenus([]);
    setSelectedMenus([]);
  };

  return (
    <>
      <div className="flex items-end space-x-4 mb-4">
        <div className="flex-1 relative">
          <label htmlFor="menu_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
            Menus Selected
          </label>
          <input
            type="text"
            id="menu_id"
            name="menu_id"
            value={
              selectedMenus.length > 0
                ? selectedMenus
                    .map((m) => (m.menu_type ? `${m.name} (${m.menu_type})` : m.name))
                    .join(', ')
                : ''
            }
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder="Menus"
            readOnly
          />
          {selectedMenus.length > 0 && (
            <button
              onClick={handleClearMenus}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 mt-7"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
        <div>
          <button
            onClick={toggleModal}
            className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
          >
            Select
          </button>
        </div>
      </div>

      {isOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-35 z-50"></div>
          <div className="fixed top-0 right-0 left-0 z-50 flex justify-center items-center w-full h-full">
            <div className="relative p-4 w-full max-w-md h-full md:h-auto">
              <div className="relative bg-white rounded-lg shadow dark:bg-gray-700">
                <div className="flex justify-between items-start p-4 rounded-t border-b dark:border-gray-600">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Select Menus
                  </h3>
                  <button onClick={toggleModal} className="text-gray-400 hover:bg-gray-200 rounded-lg text-sm p-1.5 ml-auto">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-4">
                  <input
                    type="text"
                    placeholder="Search menus..."
                    className="mb-4 pl-4 pr-4 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {isLoading ? (
                    <Spinner minHeight={100} />
                  ) : (
                    <ul className="max-h-60 overflow-y-auto">
                      {menus.map((menu) => (
                        <li key={menu.menu_id} className="cursor-pointer hover:bg-gray-100 p-2 flex items-center">
                          <input
                            type="checkbox"
                            id={`menu-${menu.menu_id}`}
                            checked={selectedMenus.some((m) => m.menu_id === menu.menu_id)}
                            onChange={() => handleToggleMenu(menu)}
                            className="mr-2"
                          />
                          <label htmlFor={`menu-${menu.menu_id}`} className="cursor-pointer">
                            {menu.menu_type ? `${menu.name} (${menu.menu_type})` : menu.name}
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="flex justify-between items-center p-4 border-t dark:border-gray-600">
                  <button
                    onClick={handleSaveSelection}
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                  >
                    Save
                  </button>
                  <button
                    onClick={toggleModal}
                    className="text-gray-700 hover:text-gray-900 text-sm px-5 py-2.5"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MenuModal;
